import { ScrollArea } from '@/components/shadcn/scroll-area'
import { IconCheckMarkOutline } from '@icons/IconCheckMarkOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import clsx from 'clsx'
import { formatDate } from 'date-fns'
import { Fragment, ReactNode } from 'react'
import { CascadingFieldLabels, JsonFormSchemaV2 } from '../FormComponent/types'
import { ComponentActionsContainer } from '../shared/ComponentActionsContainer'
import { ComponentHeader } from '../shared/ComponentHeader'
import { ComponentTitle } from '../shared/ComponentTitle'
import { getComponentHeights } from '../utils'
import { EditAction } from './EditAction'
import { ExportAction } from './ExportAction'
import { DataFormUISchema, FormData } from './types'
type TitleValue = { title: string; value: unknown }
type LabelValue = { label: string; value: unknown }

function hasTitleAndValue(input: unknown): input is TitleValue {
  if (typeof input !== 'object' || input === null) return false
  const obj = input as Record<string, unknown>
  return typeof obj.title === 'string' && 'value' in obj
}

function hasLabelAndValue(input: unknown): input is LabelValue {
  if (typeof input !== 'object' || input === null) return false
  const obj = input as Record<string, unknown>
  return typeof obj.label === 'string' && 'value' in obj
}

export function DataFormContent({
  formValues,
  uiSchema,
  tileFilters,
}: {
  formValues: FormData | Record<string, never>
  uiSchema: DataFormUISchema
  tileFilters?: React.ReactNode
}) {
  const { actions, dataDisplay, schema, canExport } = uiSchema
  const { title, description, emphasizeHeader } = schema
  const hasHeader = !!title && !!tileFilters
  const { contentHeight } = getComponentHeights(emphasizeHeader, hasHeader)
  const { type, compact = true, columns } = dataDisplay

  return (
    <div className="flex h-full flex-col">
      {title && (
        <ComponentHeader emphasize={emphasizeHeader}>
          <ComponentTitle title={title} description={description} />
          <div className="flex items-center gap-2">
            <ComponentActionsContainer className="gap-1">
              {canExport && <ExportAction formValues={formValues} />}
              {actions.handlerId && <EditAction handlerId={actions.handlerId} schema={schema} />}
              {tileFilters}
            </ComponentActionsContainer>
          </div>
        </ComponentHeader>
      )}
      <ScrollArea className="h-full">
        <div style={{ height: contentHeight }} className="flex h-full w-full flex-col justify-center">
          <div className="pt-3">
            {type === 'grid' ? (
              <GridProperties schema={schema} compact={compact} columns={columns} />
            ) : (
              <InlineProperties schema={schema} compact={compact} />
            )}
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}

function GridProperties({
  schema,
  compact,
  columns,
}: {
  schema: JsonFormSchemaV2
  compact?: boolean
  columns?: number
}) {
  return (
    <div
      style={{ gridTemplateColumns: `repeat(${columns ?? 3}, minmax(0, 1fr))` }}
      className="grid grid-cols-3 gap-1.5 rounded-lg bg-background"
    >
      {schema.properties.map((property) => (
        <div className="rounded-lg border border-muted" key={property.id}>
          <div className="grid h-full grid-cols-[40%_60%] items-center">
            <div className="flex h-full w-full justify-end rounded-l-lg bg-muted px-3 py-1 text-sm font-light">
              <span className="line-clamp-2 pr-1 pt-[6px] text-right text-xs">{property.title}</span>
            </div>
            <div className="flex h-full w-full px-3 py-1">
              <GridValueDisplay
                value={property.default}
                type={property.type}
                format={property.format}
                fieldLabels={property.fieldLabels}
                compact={compact}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function InlineProperties({ schema, compact }: { schema: JsonFormSchemaV2; compact?: boolean }) {
  return (
    <div className="flex flex-wrap gap-3">
      {schema.properties.map((property) => (
        <div className="flex gap-1" key={property.id}>
          <div className="whitespace-nowrap font-bold">{property.title}:</div>
          <div className="flex flex-col">
            <InlineValueDisplay
              value={property.default}
              type={property.type}
              format={property.format}
              fieldLabels={property.fieldLabels}
              compact={compact}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

function GridValueDisplay({
  value,
  type,
  format,
  compact,
  fieldLabels,
}: {
  value: unknown
  type?: string
  format?: string
  compact?: boolean
  fieldLabels?: CascadingFieldLabels
}) {
  const valueClass = clsx('truncate font-medium')
  if (type === 'cascading' && fieldLabels && typeof value === 'object' && value !== null) {
    const cascadingValue = value as Record<string, unknown>
    const parts: ReactNode[] = []

    const levels: Array<{ key: keyof typeof cascadingValue; label: keyof CascadingFieldLabels }> = [
      { key: 'level1', label: 'level1' },
      { key: 'level2', label: 'level2' },
      { key: 'level3', label: 'level3' },
      { key: 'level4', label: 'level4' },
      { key: 'level5', label: 'level5' },
    ]

    for (const { key, label } of levels) {
      const levelValue = cascadingValue[key]
      const levelLabel = fieldLabels[label]

      if (levelValue !== undefined && levelLabel) {
        if (Array.isArray(levelValue)) {
          parts.push(
            <>
              <span>{`[ ${levelValue.join(', ')} ]`}</span>
            </>
          )
        } else {
          parts.push(
            <>
              <span>{`${levelValue}`}</span>
            </>
          )
        }
      }
    }

    return (
      <div className="flex flex-col">
        {parts.map((part, i) => (
          <div className="flex flex-col" key={i}>
            <span className={valueClass}>{part}</span>
          </div>
        ))}
      </div>
    )
  }
  if (typeof value === 'boolean') {
    if (value) {
      return <IconCheckMarkOutline className="mx-auto self-center text-green-500" />
    }
    return <IconCloseOutline className="mx-auto self-center text-red-500" />
  }
  if (typeof value === 'number') {
    return <span className={valueClass}>{value}</span>
  }
  if (typeof value === 'string') {
    if (format === 'date') {
      return <span className={valueClass}>{formatDate(new Date(value), 'PP')}</span>
    }
    if (format === 'date-time') {
      return <span className={valueClass}>{formatDate(new Date(value), 'PP, p')}</span>
    }
    return <span className={valueClass}>{value}</span>
  }
  if (Array.isArray(value)) {
    return (
      <div className="flex w-full flex-col">
        {value.map((v, i) => (
          <GridValueDisplay key={i} value={v} compact={compact} />
        ))}
      </div>
    )
  }
  if (typeof value === 'object') {
    if (hasTitleAndValue(value)) {
      return <span className={valueClass}>{`${value.title}`}</span>
    }
    if (hasLabelAndValue(value)) {
      return <span className={valueClass}>{`${value.label}`}</span>
    }
    return (
      <div className="flex">
        {/* {value &&
          Object.entries(value).map(([key, propertyValue], index, self) => {
            const isLast = index === self.length - 1
            const isNotFirst = index !== 0
            return (
              <span key={`${key}-${value}`} className={valueClass}>
                {isNotFirst && <span>&nbsp;</span>}
                {`${propertyValue}` + (isLast ? '' : ' | ')}
              </span>
            )
          })} */}
        {value &&
          Object.entries(value).map(([key, propertyValue], i, self) => {
            const isLast = i === self.length - 1
            const isNotFirst = i !== 0
            return (
              <Fragment key={`${key}-${propertyValue}`}>
                {isNotFirst && <span>&nbsp;</span>}
                <GridValueDisplay key={i} value={propertyValue} compact={compact} />
                {!isLast && <span>&nbsp;|</span>}
              </Fragment>
            )
          })}
      </div>
    )
    // return <span>{JSON.stringify(value)}</span>
  }
}

function InlineValueDisplay({
  value,
  type,
  format,
  compact,
  fieldLabels,
}: {
  value: unknown
  type?: string
  format?: string
  compact?: boolean
  fieldLabels?: CascadingFieldLabels
}) {
  const valueClass = clsx('truncate font-medium', compact && 'max-w-[200px]')
  if (type === 'cascading' && fieldLabels && typeof value === 'object' && value !== null) {
    const cascadingValue = value as Record<string, unknown>
    const parts: ReactNode[] = []

    const levels: Array<{ key: keyof typeof cascadingValue; label: keyof CascadingFieldLabels }> = [
      { key: 'level1', label: 'level1' },
      { key: 'level2', label: 'level2' },
      { key: 'level3', label: 'level3' },
      { key: 'level4', label: 'level4' },
      { key: 'level5', label: 'level5' },
    ]

    for (const { key, label } of levels) {
      const levelValue = cascadingValue[key]
      const levelLabel = fieldLabels[label]

      if (levelValue !== undefined && levelLabel) {
        if (Array.isArray(levelValue)) {
          parts.push(
            <>
              <span>{`[ ${levelValue.join(', ')} ]`}</span>
            </>
          )
        } else {
          parts.push(
            <>
              <span>{`${levelValue}`}</span>
            </>
          )
        }
      }
    }

    return (
      <div className="flex gap-1">
        {parts.map((part, i) => (
          <span className={valueClass} key={i}>
            {part}
            {i < parts.length - 1 ? ' > ' : ''}
          </span>
        ))}
      </div>
    )
  }
  if (typeof value === 'boolean') {
    if (value) {
      return <IconCheckMarkOutline className="mx-auto text-green-500" />
    }
    return <IconCloseOutline className="mx-auto text-red-500" />
  }
  if (typeof value === 'number') {
    return <span className={valueClass}>{value}</span>
  }
  if (typeof value === 'string') {
    if (format === 'date') {
      return <span className={valueClass}>{formatDate(new Date(value), 'PP')}</span>
    }
    if (format === 'date-time') {
      return <span className={valueClass}>{formatDate(new Date(value), 'PP, p')}</span>
    }
    return <span className={valueClass}>{value}</span>
  }
  if (Array.isArray(value)) {
    return <span className={valueClass}>{value.join(', ')}</span>
  }
  if (typeof value === 'object') {
    if (hasTitleAndValue(value)) {
      return <span className={valueClass}>{`${value.title}`}</span>
    }
    if (hasLabelAndValue(value)) {
      return <span className={valueClass}>{`${value.label}`}</span>
    }
    return <span>{JSON.stringify(value)}</span>
  }
}
